import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
} from '@mui/material';
import { Person as PersonIcon, Phone as PhoneIcon, Store as StoreIcon, Edit as EditIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { registerAdmin, fetchAdmins, deleteAdmin, updateAdmin } from '../adminsApi';
import { fetchBranches } from '../branchesApi';
import { useTranslation } from 'react-i18next';

const RegisterAdmin = () => {
  const { t } = useTranslation();
  const [branches, setBranches] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [formData, setFormData] = useState({
    Name: '',
    Number: '',
    BranchId: '',
    Permission: false,
    Password: ''
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);

  useEffect(() => {
    loadBranches();
    loadAdmins();
  }, []);

  const loadBranches = async () => {
    try {
      const data = await fetchBranches();
      setBranches(data);
    } catch (error) {
      console.error('Error loading branches:', error);
      Swal.fire(t('error'), 'Failed to load branches', 'error');
    }
  };

  const loadAdmins = async () => {
    try {
      const data = await fetchAdmins();
      setAdmins(data);
    } catch (error) {
      console.error('Error loading admins:', error);
      Swal.fire(t('error'), 'Failed to load admins', 'error');
    }
  };

  // Helper function to get branch name by ID
  const getBranchName = (branchId) => {
    const branch = branches.find(b => (b.id || b.Id) === branchId);
    return branch ? (branch.name || branch.Name) : branchId;
  };

  // Helper function to strip +967 prefix
  const stripPhonePrefix = (number) => {
    if (!number) return '';
    return number.toString().replace(/^\+967/, '');
  };

  // Helper function to add +967 prefix
  const addPhonePrefix = (number) => {
    if (!number) return '';
    const cleaned = stripPhonePrefix(number);
    return cleaned ? `+967${cleaned}` : '';
  };

  const handleEdit = (admin) => {
    // Strip the prefix when loading into edit form
    setEditAdmin({
      Id: admin.Id,
      Name: admin.Name,
      Number: stripPhonePrefix(admin.Number),
      BranchId: admin.BranchId,
      Permission: admin.Permission
      // Note: No Password field - backend only updates if provided
    });
    setEditModalOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditAdmin((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editAdmin.Name || !editAdmin.Number || !editAdmin.BranchId) {
      Swal.fire(t('error'), t('fillAllRequiredFields'), 'error');
      return;
    }

    // Prepare data - only send fields that should be updated
    const submitEdit = {
      Name: editAdmin.Name,
      Number: addPhonePrefix(editAdmin.Number),
      BranchId: editAdmin.BranchId,
      Permission: editAdmin.Permission
      // Don't send Password unless user wants to change it
    };

    try {
      await updateAdmin(editAdmin.Id, submitEdit);
      Swal.fire(t('success'), t('adminUpdatedSuccessfully') || 'Admin updated successfully!', 'success');
      setEditModalOpen(false);
      setEditAdmin(null);
      loadAdmins();
    } catch (error) {
      console.error('Update error:', error);
      // Handle different error response formats
      const errorMessage = error.response?.data?.message 
        || error.response?.data 
        || error.message 
        || 'Failed to update admin';
      Swal.fire(t('error'), errorMessage, 'error');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.Name || !formData.Number || !formData.BranchId || !formData.Password) {
      Swal.fire(t('error'), t('fillAllRequiredFields'), 'error');
      return;
    }

    const submitData = {
      Name: formData.Name,
      Number: addPhonePrefix(formData.Number),
      BranchId: formData.BranchId,
      Permission: formData.Permission,
      Password: formData.Password
    };

    try {
      await registerAdmin(submitData);
      Swal.fire(t('success'), 'Admin registered successfully!', 'success');
      setModalOpen(false);
      setFormData({ Name: '', Number: '', BranchId: '', Permission: false, Password: '' });
      loadAdmins();
    } catch (error) {
      console.error('Register error:', error);
      // Handle different error response formats
      const errorMessage = error.response?.data?.message 
        || error.response?.data 
        || error.message 
        || 'Failed to register admin';
      Swal.fire(t('error'), errorMessage, 'error');
    }
  };

  const handleReset = () => {
    setFormData({ Name: '', Number: '', BranchId: '', Permission: false, Password: '' });
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: t('areYouSure') || 'Are you sure?',
      text: t('deleteAdminWarning') || "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('yesDelete') || 'Yes, delete it!',
      cancelButtonText: t('cancel') || 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await deleteAdmin(id);
        Swal.fire(t('deleted') || 'Deleted!', t('adminDeleted') || 'Admin has been deleted.', 'success');
        loadAdmins();
      } catch (error) {
        console.error('Delete error:', error);
        const errorMessage = error.response?.data?.message 
          || error.response?.data 
          || error.message 
          || 'Failed to delete admin';
        Swal.fire(t('error'), errorMessage, 'error');
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">{t('adminsList') || 'Admins List'}</Typography>
        <Button variant="contained" color="primary" onClick={() => setModalOpen(true)}>
          {t('addAdmin') || 'Add Admin'}
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ borderBottom: '2px solid #ddd', padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>#</th>
                  <th style={{ borderBottom: '2px solid #ddd', padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>{t('nameLabel') || 'Name'}</th>
                  <th style={{ borderBottom: '2px solid #ddd', padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>{t('phoneNumberLabel') || 'Phone Number'}</th>
                  <th style={{ borderBottom: '2px solid #ddd', padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>{t('branchLabel') || 'Branch'}</th>
                  <th style={{ borderBottom: '2px solid #ddd', padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>{t('hasPermissionLabel') || 'Permission'}</th>
                  <th style={{ borderBottom: '2px solid #ddd', padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                      {t('noAdminsFound') || 'No admins found'}
                    </td>
                  </tr>
                ) : (
                  admins.map((admin, idx) => (
                    <tr key={admin.Id}>
                      <td style={{ borderBottom: '1px solid #eee', padding: '12px 8px' }}>{idx + 1}</td>
                      <td style={{ borderBottom: '1px solid #eee', padding: '12px 8px' }}>{admin.Name}</td>
                      <td style={{ borderBottom: '1px solid #eee', padding: '12px 8px' }}>{admin.Number}</td>
                      <td style={{ borderBottom: '1px solid #eee', padding: '12px 8px' }}>{getBranchName(admin.BranchId)}</td>
                      <td style={{ borderBottom: '1px solid #eee', padding: '12px 8px' }}>
                        <span style={{ 
                          padding: '4px 12px', 
                          borderRadius: '12px', 
                          backgroundColor: admin.Permission ? '#4caf50' : '#f44336',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 500
                        }}>
                          {admin.Permission ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td style={{ borderBottom: '1px solid #eee', padding: '12px 8px' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button 
                            color="primary" 
                            variant="outlined" 
                            size="small" 
                            onClick={() => handleEdit(admin)} 
                            startIcon={<EditIcon />}
                          >
                            {t('edit') || 'Edit'}
                          </Button>
                          <Button 
                            color="error" 
                            variant="outlined" 
                            size="small" 
                            onClick={() => handleDelete(admin.Id)}
                          >
                            {t('delete') || 'Delete'}
                          </Button>
                        </Box>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
        </CardContent>
      </Card>

      {/* Modal for Add Admin */}
      {modalOpen && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100vw', 
          height: '100vh', 
          bgcolor: 'rgba(0,0,0,0.5)', 
          zIndex: 9999, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 2
        }}>
          <Card sx={{ minWidth: 400, maxWidth: 600, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <CardHeader
              title={t('registerNewAdmin') || 'Register New Admin'}
              sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}
            />
            <form onSubmit={handleSubmit}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('nameLabel') || 'Name'}
                      name="Name"
                      placeholder={t('enterName') || 'Enter name'}
                      value={formData.Name}
                      onChange={handleChange}
                      required
                      InputProps={{
                        startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('phoneNumberLabel') || 'Phone Number'}
                      name="Number"
                      placeholder="7XXXXXXXX"
                      value={formData.Number}
                      onChange={e => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                        handleChange({
                          target: { name: 'Number', value: digits, type: 'text' }
                        });
                      }}
                      required
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <PhoneIcon sx={{ color: 'action.active', mr: 0.5 }} />
                            <Typography>+967</Typography>
                          </Box>
                        ),
                      }}
                      inputProps={{ maxLength: 9 }}
                      helperText="Enter 9 digits without +967"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>{t('branchLabel') || 'Branch'}</InputLabel>
                      <Select
                        name="BranchId"
                        value={formData.BranchId}
                        onChange={handleChange}
                        label={t('branchLabel') || 'Branch'}
                      >
                        <MenuItem value="">
                          <em>{t('selectBranch') || 'Select Branch'}</em>
                        </MenuItem>
                        {branches.map((branch) => (
                          <MenuItem key={branch.id || branch.Id} value={branch.id || branch.Id}>
                            {branch.name || branch.Name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('passwordLabel') || 'Password'}
                      name="Password"
                      type="password"
                      placeholder={t('enterPassword') || 'Enter password'}
                      value={formData.Password}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.Permission}
                          onChange={handleChange}
                          name="Permission"
                        />
                      }
                      label={t('hasPermissionLabel') || 'Has Admin Permission'}
                    />
                  </Grid>
                </Grid>
              </CardContent>
              <Box sx={{ p: 2, display: 'flex', gap: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button type="submit" variant="contained" color="primary">
                  {t('registerAdmin') || 'Register Admin'}
                </Button>
                <Button type="button" variant="outlined" onClick={() => { handleReset(); setModalOpen(false); }}>
                  {t('cancel') || 'Cancel'}
                </Button>
              </Box>
            </form>
          </Card>
        </Box>
      )}

      {/* Modal for Edit Admin */}
      {editModalOpen && editAdmin && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100vw', 
          height: '100vh', 
          bgcolor: 'rgba(0,0,0,0.5)', 
          zIndex: 9999, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 2
        }}>
          <Card sx={{ minWidth: 400, maxWidth: 600, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <CardHeader
              title={t('editAdmin') || 'Edit Admin'}
              sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}
            />
            <form onSubmit={handleEditSubmit}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('nameLabel') || 'Name'}
                      name="Name"
                      placeholder={t('enterName') || 'Enter name'}
                      value={editAdmin.Name}
                      onChange={handleEditChange}
                      required
                      InputProps={{
                        startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('phoneNumberLabel') || 'Phone Number'}
                      name="Number"
                      placeholder="7XXXXXXXX"
                      value={editAdmin.Number}
                      onChange={e => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                        handleEditChange({
                          target: { name: 'Number', value: digits, type: 'text' }
                        });
                      }}
                      required
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <PhoneIcon sx={{ color: 'action.active', mr: 0.5 }} />
                            <Typography>+967</Typography>
                          </Box>
                        ),
                      }}
                      inputProps={{ maxLength: 9 }}
                      helperText="Enter 9 digits without +967"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>{t('branchLabel') || 'Branch'}</InputLabel>
                      <Select
                        name="BranchId"
                        value={editAdmin.BranchId}
                        onChange={handleEditChange}
                        label={t('branchLabel') || 'Branch'}
                      >
                        <MenuItem value="">
                          <em>{t('selectBranch') || 'Select Branch'}</em>
                        </MenuItem>
                        {branches.map((branch) => (
                          <MenuItem key={branch.id || branch.Id} value={branch.id || branch.Id}>
                            {branch.name || branch.Name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editAdmin.Permission}
                          onChange={handleEditChange}
                          name="Permission"
                        />
                      }
                      label={t('hasPermissionLabel') || 'Has Admin Permission'}
                    />
                  </Grid>
                </Grid>
              </CardContent>
              <Box sx={{ p: 2, display: 'flex', gap: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button type="submit" variant="contained" color="primary">
                  {t('updateAdmin') || 'Update Admin'}
                </Button>
                <Button type="button" variant="outlined" onClick={() => { setEditModalOpen(false); setEditAdmin(null); }}>
                  {t('cancel') || 'Cancel'}
                </Button>
              </Box>
            </form>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default RegisterAdmin;